import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Box, Button, Typography, TextField, InputLabel, FormControlLabel, Checkbox } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { IFailureTypeForm, IFailureTypeValues } from "./FailureTypeCreationFormInterfaces";
import { failureTypeFormValidationSchema } from "validations/schemas";
import { useStyles } from "./FailureTypeCreationForm.styles";
import { FailureDetailsEntity } from "entities/FailureDetailsEntity/FailureDetailsEntity";
import { pickRandomColor } from "utilities/utilities";

export const FailureTypeCreationForm = ({ handleClose, corporateId }: IFailureTypeForm) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { apolloMutationError, addFailureType } = FailureDetailsEntity.InsertFailureType();

    const [isLocationSpecificCheck, setIsLocationSpecificCheck] = useState(false);

    const handleIsLocationSpecificChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setIsLocationSpecificCheck(event.target.checked);
    }, []);

    const colorCode = pickRandomColor();

    const handleAddFailureTypeSubmit = useCallback(
        async (values: IFailureTypeValues) => {
            const failureTypeValues = {
                name: values.name,
                corporate_id: corporateId,
                is_location_specific: isLocationSpecificCheck,
                color_code: colorCode,
            };

            try {
                const response = await addFailureType({
                    variables: { failure_type: failureTypeValues },
                });

                if (response) {
                    handleClose();
                }
            } catch {
                return;
            }
        },
        [addFailureType, handleClose, corporateId, isLocationSpecificCheck, colorCode],
    );

    const initFormValues: IFailureTypeValues = {
        name: "",
        coporate_id: corporateId,
        is_location_specific: false,
        color_code: "",
    };

    return (
        <Formik
            initialValues={initFormValues}
            validationSchema={failureTypeFormValidationSchema}
            onSubmit={handleAddFailureTypeSubmit}
        >
            {({ isSubmitting, errors, touched, values, handleChange, setFieldValue }) => {
                return (
                    <Form className={classes.formContainer}>
                        <Box className={classes.boxContainer}>
                            <InputLabel id="outlined-failure-type-name-label">
                                <Typography variant="subtitle2" className={classes.formLabel}>
                                    {t("failure_details_setup.add_failure_types_modal.name")}
                                </Typography>
                            </InputLabel>
                            <TextField
                                aria-labelledby="outlined-failure-type-name-label"
                                fullWidth
                                id="outlined-failure-type-name-input"
                                className={classes.textField}
                                name="name"
                                type="text"
                                variant="outlined"
                                autoFocus={true}
                                value={values.name}
                                onChange={handleChange}
                                error={touched.name && Boolean(errors.name)}
                                helperText={touched.name && errors.name && t(errors.name)}
                            />
                        </Box>
                        <Box className={classes.boxContainer}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isLocationSpecificCheck}
                                        onChange={handleIsLocationSpecificChange}
                                        name="is_location_specific"
                                        className={classes.checkbox}
                                    />
                                }
                                label={
                                    <Typography variant="error" className={classes.checkboxLabel}>
                                        {t("failure_details_setup.add_failure_types_modal.is_location_specific")}
                                    </Typography>
                                }
                                labelPlacement="end"
                            />
                        </Box>
                        <p style={{ display: apolloMutationError ? "block" : "none", color: "red" }}>
                            {apolloMutationError?.message.includes("uq_failure_type_name")
                                ? t("failure_details_setup.add_failure_types_modal.existing_failure_type_name_error")
                                : apolloMutationError?.message.includes(
                                      "check constraint of an insert/update permission has failed",
                                  )
                                ? t("failure_details_setup.add_failure_types_modal.no_corporate_id_failure_type_error")
                                : t("")}
                        </p>
                        <div className={classes.buttonsContainer}>
                            <Button className={classes.cancelButton} variant="text" onClick={handleClose}>
                                {t("failure_details_setup.add_failure_types_modal.cancel")}
                            </Button>
                            <Button
                                className={classes.addButton}
                                variant="outlined"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                <AddIcon /> {t("failure_details_setup.add_failure_types_modal.save")}
                            </Button>
                        </div>
                    </Form>
                );
            }}
        </Formik>
    );
};
